import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useGetA2pRegistrationQuery,
  useSaveA2pDraftMutation,
  useSubmitA2pCustomerProfileMutation,
  useGenerateA2pContentMutation,
  useGetHomeQuery,
  useGetA2pPricingQuery,
  usePurchaseAssistedA2pMutation,
} from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import A2pRegistrationWizard from "./A2pRegistrationWizard";
import A2pCampaignsSection from "./A2pCampaignsSection";
import RegistrationStatusStep from "./RegistrationStatusStep";
import A2pPricingSummary from "./A2pPricingSummary";
import { BUSINESS_TYPES, INDUSTRIES, JOB_POSITIONS, USE_CASES } from "./a2pConstants";

const EDITABLE_STATUSES = ["NOT_STARTED", "PLATFORM_REJECTED", "PROFILE_REJECTED", "BRAND_FAILED", "CAMPAIGN_FAILED", "CANCELLED"];

const registrationFormSchema = z
  .object({
    businessName: z.string().min(1, "Business name is required"),
    businessType: z.string().min(1, "Business type is required"),
    ein: z.string().optional(),
    proofMobile: z.string().optional(),
    businessIndustry: z.string().min(1, "Industry is required"),
    websiteUrl: z.string().min(1, "Website is required"),
    regionsOfOperation: z.string(),
    street: z.string().min(1, "Street is required"),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State is required"),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
    repFirstName: z.string().min(1, "First name is required"),
    repLastName: z.string().min(1, "Last name is required"),
    repEmail: z.string().email("A valid email is required"),
    repPhone: z.string().min(1, "Phone is required"),
    repTitle: z.string().min(1, "Title is required"),
    repJobPosition: z.string().min(1, "Job position is required"),
    skipAutomaticSecVet: z.boolean(),
    useCase: z.string().optional(),
    campaignDescription: z.string().optional(),
    messageFlow: z.string().optional(),
    messageSample1: z.string().optional(),
    messageSample2: z.string().optional(),
    optInMessage: z.string().optional(),
    optOutMessage: z.string().optional(),
    helpMessage: z.string().optional(),
    privacyPolicyConfirmed: z.boolean(),
    leadFormOptInConfirmed: z.boolean(),
  })
  .superRefine((values, context) => {
    if (values.businessType === "SOLE_PROPRIETOR") {
      if (!values.proofMobile?.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["proofMobile"],
          message: "The owner mobile number is required for sole proprietors",
        });
      }
    } else if (!values.ein?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ein"],
        message: "EIN is required",
      });
    }
    if (!values.privacyPolicyConfirmed) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["privacyPolicyConfirmed"],
        message: "Please confirm your website privacy policy language",
      });
    }
    if (!values.leadFormOptInConfirmed) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["leadFormOptInConfirmed"],
        message: "Please confirm your lead form opt-in language",
      });
    }
  });

type RegistrationFormValues = z.infer<typeof registrationFormSchema>;

export default function A2pRegistrationForm() {
  const { data: registration, isLoading } = useGetA2pRegistrationQuery();
  const { data: home } = useGetHomeQuery();
  const [saveDraft, { isLoading: isSavingDraft }] = useSaveA2pDraftMutation();
  const [submitProfile, { isLoading: isSubmitting }] = useSubmitA2pCustomerProfileMutation();
  const [generateContent, { isLoading: isGenerating }] = useGenerateA2pContentMutation();

  const { data: a2pPricing } = useGetA2pPricingQuery();
  const [purchaseAssisted, { isLoading: isPurchasingAssisted }] = usePurchaseAssistedA2pMutation();

  const [useWizard, setUseWizard] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selfServeChosen, setSelfServeChosen] = useState(false);
  const [assistedConsent, setAssistedConsent] = useState(false);

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationFormSchema),
    defaultValues: {
      businessName: "",
      businessType: "",
      ein: "",
      proofMobile: "",
      businessIndustry: "",
      websiteUrl: "",
      regionsOfOperation: "US_AND_CANADA",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      repFirstName: "",
      repLastName: "",
      repEmail: "",
      repPhone: "",
      repTitle: "",
      repJobPosition: "",
      skipAutomaticSecVet: false,
      useCase: "",
      campaignDescription: "",
      messageFlow: "",
      messageSample1: "",
      messageSample2: "",
      optInMessage: "",
      optOutMessage: "",
      helpMessage: "",
      privacyPolicyConfirmed: false,
      leadFormOptInConfirmed: false,
    },
  });

  const selectedBusinessType = form.watch("businessType");
  const soleProprietor = selectedBusinessType === "SOLE_PROPRIETOR";
  const assistedActive = registration?.submissionMode === "ASSISTED" && !!registration.assistedPurchasedAt;
  const showChoiceScreen =
    !selfServeChosen &&
    !assistedActive &&
    registration?.status === "NOT_STARTED" &&
    !registration.businessName;

  const handlePurchaseAssisted = async () => {
    if (!assistedConsent) {
      toast.error("Please confirm the authorization first");
      return;
    }
    try {
      await purchaseAssisted().unwrap();
      toast.success("Assisted registration purchased. Book your call below and we take it from there.");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to purchase assisted registration");
    }
  };
  const skipSecVet = !!form.watch("skipAutomaticSecVet");
  const editable = !registration || EDITABLE_STATUSES.includes(registration.status);

  const buildPayload = (values: RegistrationFormValues): Partial<A2pRegistration> => {
    const { privacyPolicyConfirmed, leadFormOptInConfirmed, ...rest } = values;
    return {
      ...rest,
      submissionMode: "AUTO_CHAIN",
      privacyPolicyAttestation: privacyPolicyConfirmed ? "CONFIRMED" : "PENDING",
      leadFormOptInAttestation: leadFormOptInConfirmed ? "CONFIRMED" : "PENDING",
    };
  };

  const handleSaveDraft = async () => {
    try {
      await saveDraft(buildPayload(form.getValues())).unwrap();
      toast.success("Draft saved");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to save draft");
    }
  };

  const handleGenerate = async (force: boolean) => {
    try {
      await saveDraft(buildPayload(form.getValues())).unwrap();
      await generateContent({ force }).unwrap();
      setAdvancedOpen(true);
      toast.success("Carrier submission content generated. Review it below.");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to generate content");
    }
  };

  const onSubmit = async (values: RegistrationFormValues) => {
    try {
      const payload = buildPayload(values);
      await saveDraft(payload).unwrap();
      await generateContent().unwrap();
      await submitProfile(payload).unwrap();
      toast.success("Registration submitted. Our team reviews it within one business day, then every following step runs automatically.");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit registration");
    }
  };

  useEffect(() => {
    if (registration) {
      form.reset({
        businessName: registration.businessName || "",
        businessType: registration.businessType || "",
        ein: registration.ein || "",
        proofMobile: registration.proofMobile || "",
        businessIndustry: registration.businessIndustry || "",
        websiteUrl: registration.websiteUrl || "",
        regionsOfOperation: registration.regionsOfOperation || "US_AND_CANADA",
        street: registration.street || "",
        city: registration.city || "",
        state: registration.state || "",
        postalCode: registration.postalCode || "",
        country: registration.country || "US",
        repFirstName: registration.repFirstName || "",
        repLastName: registration.repLastName || "",
        repEmail: registration.repEmail || "",
        repPhone: registration.repPhone || "",
        repTitle: registration.repTitle || "",
        repJobPosition: registration.repJobPosition || "",
        skipAutomaticSecVet: registration.skipAutomaticSecVet ?? false,
        useCase: registration.useCase || "",
        campaignDescription: registration.campaignDescription || "",
        messageFlow: registration.messageFlow || "",
        messageSample1: registration.messageSample1 || "",
        messageSample2: registration.messageSample2 || "",
        optInMessage: registration.optInMessage || "",
        optOutMessage: registration.optOutMessage || "",
        helpMessage: registration.helpMessage || "",
        privacyPolicyConfirmed: registration.privacyPolicyAttestation === "CONFIRMED",
        leadFormOptInConfirmed: registration.leadFormOptInAttestation === "CONFIRMED",
      });
    }
  }, [registration]);

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (useWizard) {
    return (
      <div className="space-y-4">
        <Button variant="link" className="px-0" onClick={() => setUseWizard(false)}>
          Back to the simple registration form
        </Button>
        <A2pRegistrationWizard />
      </div>
    );
  }

  if (assistedActive && registration) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>We Are Handling Your Registration</CardTitle>
          <CardDescription>
            You purchased assisted registration. Our team completes the carrier paperwork with you
            and keeps you posted at every step. Texting unlocks once the registration is approved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {a2pPricing?.assistedSchedulingUrl ? (
            <Button asChild>
              <a href={a2pPricing.assistedSchedulingUrl} target="_blank" rel="noreferrer">
                Book your onboarding call
              </a>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Our team will reach out shortly to schedule your onboarding call.
            </p>
          )}
          <RegistrationStatusStep registration={registration} />
        </CardContent>
      </Card>
    );
  }

  if (showChoiceScreen) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Do It Yourself</CardTitle>
            <CardDescription>
              Fill in one business form and we submit everything automatically as each step is
              approved. Free; carrier fees apply as usual.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setSelfServeChosen(true)}>Start Registration</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>We Handle It For You</CardTitle>
            <CardDescription>
              Book a call and our team completes and submits the whole registration on your behalf.
              One-time fee of ${a2pPricing ? a2pPricing.assistedRegistration.toFixed(2) : "..."}
              {" "}charged from your balance, plus the usual carrier fees.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start gap-3 text-sm">
              <Checkbox
                checked={assistedConsent}
                onCheckedChange={(checked) => setAssistedConsent(checked === true)}
                className="mt-0.5"
              />
              <span>
                I authorize the platform team to complete and submit the carrier registration on my
                behalf and to charge the one-time fee from my balance.
              </span>
            </label>
            <Button disabled={isPurchasingAssisted} onClick={handlePurchaseAssisted}>
              {isPurchasingAssisted && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Purchase Assisted Registration
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!editable && registration) {
    return (
      <div className="space-y-4">
        {registration.status === "PLATFORM_REVIEW" && (
          <Alert>
            <AlertDescription>
              Your registration is being reviewed by our team before it goes to the carrier.
              Reviews are completed within one business day and you will be notified either way.
            </AlertDescription>
          </Alert>
        )}
        <RegistrationStatusStep registration={registration} />
        {["BRAND_APPROVED", "CAMPAIGN_PENDING", "COMPLETED"].includes(registration.status) && (
          <A2pCampaignsSection />
        )}
        <Button variant="link" className="px-0" onClick={() => setUseWizard(true)}>
          Open the step-by-step wizard
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Text Messaging Registration</CardTitle>
        <CardDescription>
          US carriers require every business to register before it can send texts. Tell us about
          your business and we handle the rest: we write the carrier paperwork for you and submit
          each step automatically as the previous one is approved.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            {registration?.rejectionReason && (
              <Alert variant="destructive">
                <AlertDescription>{registration.rejectionReason}</AlertDescription>
              </Alert>
            )}

            {registration?.status === "PLATFORM_REJECTED" && (
              <div className="rounded-lg border p-4 text-sm space-y-2">
                <p className="font-medium">Common fixes before resubmitting</p>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  <li>
                    Your website needs a privacy policy stating that mobile contact information is
                    not shared with third parties for marketing purposes.
                  </li>
                  <li>
                    Lead forms that collect phone numbers need consent language, for example: "By
                    submitting this form you agree to receive text messages from us about your
                    inquiry. Message frequency varies. Message and data rates may apply. Reply STOP
                    to opt out."
                  </li>
                  <li>
                    The legal business name and EIN must match your IRS records exactly, including
                    punctuation and suffixes like LLC or Inc.
                  </li>
                  <li>Your website must be live and reachable at the address you provided.</li>
                </ul>
                <p className="text-muted-foreground">Resubmitting after our review is free.</p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold mb-3">Business</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="businessName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Legal Business Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="businessType" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {BUSINESS_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                {!soleProprietor && (
                  <FormField control={form.control} name="ein" render={({ field }) => (
                    <FormItem>
                      <FormLabel>EIN (Tax ID) *</FormLabel>
                      <FormControl><Input placeholder="XX-XXXXXXX" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
                {soleProprietor && (
                  <FormField control={form.control} name="proofMobile" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Owner mobile (for verification) *</FormLabel>
                      <FormControl><Input placeholder="+1..." {...field} /></FormControl>
                      <p className="text-xs text-muted-foreground">
                        A personal US or Canadian mobile, not a virtual or VoIP number. We text a code you reply to.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
                <FormField control={form.control} name="businessIndustry" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {INDUSTRIES.map((industry) => (
                          <SelectItem key={industry.value} value={industry.value}>{industry.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="websiteUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website *</FormLabel>
                    <FormControl><Input placeholder="https://" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Business Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="street" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="city" render={({ field }) => (
                  <FormItem>
                    <FormLabel>City *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="state" render={({ field }) => (
                  <FormItem>
                    <FormLabel>State *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="postalCode" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="country" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Contact Person</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="repFirstName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repLastName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repEmail" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl><Input type="email" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repPhone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone *</FormLabel>
                    <FormControl><Input placeholder="+1..." {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repTitle" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="repJobPosition" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Position *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {JOB_POSITIONS.map((position) => (
                          <SelectItem key={position.value} value={position.value}>{position.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <FormField control={form.control} name="skipAutomaticSecVet" render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel>Low volume sender</FormLabel>
                  <p className="text-xs text-muted-foreground">
                    Sending under 6,000 message segments a day. Lower fees and lighter vetting.
                  </p>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )} />

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="ghost" className="px-0">
                    <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
                    Advanced: carrier submission content
                  </Button>
                </CollapsibleTrigger>
                <Button type="button" variant="outline" size="sm" disabled={isGenerating} onClick={() => handleGenerate(true)}>
                  {isGenerating && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Generate from business info
                </Button>
              </div>
              <CollapsibleContent className="space-y-4 pt-3">
                <p className="text-xs text-muted-foreground">
                  We write this content for you from your business info and submit it to the
                  carrier. Edit anything you want to change before submitting.
                </p>
                <FormField control={form.control} name="useCase" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Use Case</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Generated automatically" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {USE_CASES.map((useCase) => (
                          <SelectItem key={useCase.value} value={useCase.value}>{useCase.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="campaignDescription" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Campaign Description</FormLabel>
                    <FormControl><Textarea rows={3} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="messageFlow" render={({ field }) => (
                  <FormItem>
                    <FormLabel>How Contacts Opt In</FormLabel>
                    <FormControl><Textarea rows={3} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="messageSample1" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message Sample 1</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="messageSample2" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message Sample 2</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="optInMessage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opt-In Confirmation Message</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="optOutMessage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opt-Out (STOP) Reply</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="helpMessage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>HELP Reply</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </CollapsibleContent>
            </Collapsible>

            <div className="space-y-3">
              <FormField control={form.control} name="privacyPolicyConfirmed" render={({ field }) => (
                <FormItem className="flex items-start gap-3 rounded-lg border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                  </FormControl>
                  <div className="space-y-1">
                    <FormLabel className="font-normal">
                      My website has a privacy policy that states we do not share mobile contact
                      information with third parties for marketing purposes.
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )} />
              <FormField control={form.control} name="leadFormOptInConfirmed" render={({ field }) => (
                <FormItem className="flex items-start gap-3 rounded-lg border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                  </FormControl>
                  <div className="space-y-1">
                    <FormLabel className="font-normal">
                      Forms where contacts leave their phone number include text messaging consent
                      language, or we collect consent verbally or in person.
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )} />
            </div>

            <A2pPricingSummary
              soleProprietor={soleProprietor}
              skipAutomaticSecVet={skipSecVet}
              balance={home?.activeOrganisation?.balance}
            />

            <p className="text-xs text-muted-foreground">
              By starting registration you authorize us to submit each stage to the carrier as the
              previous one is approved and to charge the listed fees from your balance
              automatically. We notify you at every step.
            </p>

            <div className="flex items-center justify-between">
              <Button type="button" variant="link" className="px-0" onClick={() => setUseWizard(true)}>
                Prefer the step-by-step wizard?
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={isSavingDraft} onClick={handleSaveDraft}>
                  {isSavingDraft && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Save Draft
                </Button>
                <Button type="submit" disabled={isSubmitting || isGenerating}>
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Start Registration
                </Button>
              </div>
            </div>
          </CardContent>
        </form>
      </Form>
    </Card>
  );
}
